import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import es from './locales/es.json';
import en from './locales/en.json';
import it from './locales/it.json';
import de from './locales/de.json';
import fr from './locales/fr.json';

export const languages = [
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
];

// Initialize i18n synchronously with all translations - v2
const base: Record<string, Record<string, unknown>> = { es, en, it, de, fr } as Record<string, Record<string, unknown>>;

// Section translation files: each exports { es: {...}, en: {...}, it, de, fr }
const sectionModules = import.meta.glob('./locales/sections/*.json', { eager: true }) as Record<
  string,
  { default: Record<string, Record<string, unknown>> }
>;

for (const mod of Object.values(sectionModules)) {
  const data = mod.default ?? (mod as unknown as Record<string, Record<string, unknown>>);
  for (const lng of ['es', 'en', 'it', 'de', 'fr']) {
    if (data[lng]) Object.assign(base[lng], data[lng]);
  }
}

const resources = {
  es: { translation: base.es },
  en: { translation: base.en },
  it: { translation: base.it },
  de: { translation: base.de },
  fr: { translation: base.fr },
};


i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    lng: undefined,
    fallbackLng: 'es',
    supportedLngs: ['es', 'en', 'it', 'de', 'fr'],
    nonExplicitSupportedLngs: true,
    load: 'languageOnly',
    interpolation: {
      escapeValue: false,
    },
    detection: {
      // Solo idioma guardado; si no hay, se usa 'es' y luego geolocalización por país
      order: ['path', 'localStorage'],
      lookupFromPathIndex: 0,
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },
    react: {
      useSuspense: false,
    },
    returnEmptyString: false,
    returnNull: false,
    keySeparator: '.',
    nsSeparator: ':',
  });

// Mapa país -> idioma (por defecto español)
const COUNTRY_LANG: Record<string, string> = {
  ES: 'es', MX: 'es', AR: 'es', CO: 'es', CL: 'es', PE: 'es', VE: 'es', EC: 'es',
  UY: 'es', PY: 'es', BO: 'es', CR: 'es', PA: 'es', DO: 'es', GT: 'es', HN: 'es',
  SV: 'es', NI: 'es', CU: 'es', PR: 'es',
  IT: 'it', SM: 'it', VA: 'it',
  FR: 'fr', BE: 'fr', MC: 'fr', LU: 'fr', SN: 'fr', CI: 'fr', MA: 'fr', TN: 'fr', DZ: 'fr',
  DE: 'de', AT: 'de', CH: 'de', LI: 'de',
  GB: 'en', IE: 'en', US: 'en', CA: 'en', AU: 'en', NZ: 'en', ZA: 'en', IN: 'en',
  NL: 'en', SE: 'en', NO: 'en', DK: 'en', FI: 'en', PL: 'en', PT: 'en', GR: 'en',
};

const hasStoredLang = () => {
  try {
    return !!localStorage.getItem('i18nextLng');
  } catch {
    return false;
  }
};

// Geolocalización por IP: solo la primera visita (sin idioma guardado)
// Nunca si la URL ya trae idioma (/en/...): la URL manda
const urlHasLang = typeof window !== 'undefined' && /^\/(es|en|it|de|fr)(\/|$)/.test(window.location.pathname);
if (typeof window !== 'undefined' && !urlHasLang && !hasStoredLang()) {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 2500);
  fetch('https://ipapi.co/json/', { signal: controller.signal })
    .then((r) => (r.ok ? r.json() : null))
    .then((data) => {
      const country = data?.country_code || data?.country;
      const lang = country ? COUNTRY_LANG[String(country).toUpperCase()] : undefined;
      if (lang && lang !== i18n.language) i18n.changeLanguage(lang);
    })
    .catch(() => {
      /* se mantiene español por defecto */
    })
    .finally(() => window.clearTimeout(timeout));
}

export default i18n;

